import type { ClaimSpiritMachine, ClaimSpiritContext, SpiritInfo } from "../claimSpiritMachine"

import { useEffect } from "react"
import { useAtomValue, useSetAtom } from 'jotai'
import { queryClientAtom } from "jotai/query"
import { atomWithMachine } from 'jotai/xstate'
import { createMachine } from "xstate"
import { isRight } from "fp-ts/lib/Either"
import Decimal from "decimal.js"
import * as R from "ramda"

import { signMessageWithSigner } from "@/utils/signMessage"
import { waitSignAndSend } from "@/utils/signAndSend"
import { useRestoreLastSelectedAccount, currentAccountAtom, signerAtom } from "@/features/identity/atoms"
import { useAutoConnect, websocketConnectionMachineAtom } from "@/features/parachain/atoms"
import { trpcVanilla, trpcQuery } from "@/providers/trpc"

import { mcpEndpointPrefixAtom, pwapiEndpointPrefixAtom } from "./hosts"
import { makeConfig, makeOptions } from "../claimSpiritMachine"
import * as requests from '../requests'
import { useRefCode } from "@/features/referrals/useRefCode"


const checkState = async (ctx: ClaimSpiritContext) => {
  const { api, address, queryClient, pwApiPrefix, mcpPrefix } = ctx
  if (!api || !address) {
    throw new Error('Not yet connected to chain.')
  }

  const queryKey = trpcQuery.users.profile.getQueryKey({ address })
  const [profile, canClaimSpiritsQuery] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: [queryKey[0], { ...queryKey[1], "type": "query"}],
      queryFn: () => trpcVanilla.users.profile.query({ address }),
      staleTime: 5_000,
    }),
    api.query.pwNftSale.canClaimSpirits(),
  ])
  const canClaimSpirits = canClaimSpiritsQuery && canClaimSpiritsQuery.isTrue
  const linked = R.fromPairs(R.map(i => [R.prop('code', i), 1], profile?.linkedIdentity ?? {}))

  if (profile?.linkedIdentity.length && profile.spirit) {
    let spiritCollection: SpiritInfo[] = []
    const spiritCollectionId = await api.query.pwNftSale.spiritCollectionId()

    if (spiritCollectionId?.isSome) {
      const spirit = await api.query.uniques.account.entries(
        address,
        spiritCollectionId.unwrap().toNumber()
      )
      if (spirit) {
        spiritCollection = spirit.map(([key, _value]) => ({
          account: key.args[0]?.toString() as string,
          collectionId: key.args[1]?.toJSON() as number,
          nftId: key.args[2]?.toJSON() as number,
          formattedNftId: key.args[2]?.toJSON() as number + 1,
        }))
      }
    }
    return {linked, spirit: profile.spirit, spiritCollection, canClaimSpirits}
  }
  if (!profile?.linkedIdentity.length) {
    return {canClaimSpirits}
  }
  if (!profile.spirit) {
    return {linked, spirit: null, canClaimSpirits}
  }
  throw new Error()
}

const createSpirit = async (ctx: ClaimSpiritContext) => {
  const { signer, address, invited, pwApiPrefix, refCode } = ctx
  if (!signer || !address) {
    throw new Error('Not yet connected to chain.')
  }
  const signConfig = await requests.getMcpSignConfigData(pwApiPrefix)
  const message = signConfig?.mcp_sign_messages?.for_create_spirit
  if (address && message) {
    const mcp_msg_signature = await signMessageWithSigner(signer, address, message)
    const result = await requests.createSpirit(
      pwApiPrefix, address, mcp_msg_signature, invited, refCode
    )
    if (isRight(result)) {
      return result.right.spirit
    }
  }
  throw new Error('createSpirit failed')
}

const updateSpirit = async (ctx: ClaimSpiritContext) => {
  const { signer, address, pwApiPrefix } = ctx
  if (!signer || !address) {
    throw new Error('Not yet connected to chain.')
  }
  const signConfig = await requests.getMcpSignConfigData(pwApiPrefix)
  const message = signConfig?.mcp_sign_messages?.for_update_spirit
  if (address && message) {
    const mcp_msg_signature = await signMessageWithSigner(signer, address, message)
    const result = await requests.updateSpirit(pwApiPrefix, address, mcp_msg_signature)
    if (isRight(result)) {
      const maxRetries = 100
      let retries = 0
      let secs = 1
      while (retries < maxRetries) {
        const attrs = await requests.fetchSpiritAttributes(pwApiPrefix, address)
        if (isRight(attrs)) {
          const data = attrs.right
          const t1 = new Date(data.stats_refreshed_at).getTime()
          const t2 = new Date(data.extra.stats_refreshing_at).getTime()
          const isRefreshing = t1 < t2
          if (!isRefreshing) {
            return data
          }
        }
        await new Promise((resolve) => setTimeout(resolve, secs * 1000))
        secs *= 2
        retries += 1
      }
    }
  }
  throw new Error('updateSpirit failed')
}

const claimSpirit = async (ctx: ClaimSpiritContext) => {
  const { api, signer, address, pwApiPrefix } = ctx
  if (!api) {
    throw new Error('Not yet connected to chain.')
  }
  if (!address) {
    throw new Error('address is required')
  }
  const result = await requests.getSpiritTicketData(pwApiPrefix, address)
  if (isRight(result)) {
    try {
      const extrinsic = api.tx.pwNftSale.redeemSpirit(result.right.signature)
      if (extrinsic) {
        await waitSignAndSend({
          account: address,
          api,
          extrinsic,
          signer,
        })

        const spiritCollectionId = await api.query.pwNftSale.spiritCollectionId()

        let countdown = 5
        let succeed = false
        while (countdown-- > 0) {
          if (spiritCollectionId?.isSome) {
            const spirit = await api.query.uniques.account.entries(
              address,
              spiritCollectionId.unwrap().toNumber()
            )
            if (spirit) {
              succeed = true
              break
            }
            await new Promise(resolve => setTimeout(resolve, 10*1000))
          }
        }
        if (!succeed) {
          // TODO huh?
        }
      }
    } catch (err) {
      console.error('Claim spirit error: ', err)
      throw Array.isArray(err) ? err[0] : err
    }
  }
}

const removeSpirit = async (address?: string) => {
  // return await axios.delete(`${PWAPI_ENDPOINT_PREFIX}/spirits/${address}`)
}

export const claimSpiritMachineAtom = atomWithMachine<ClaimSpiritMachine>((get) => {
  const account = get(currentAccountAtom)
  const queryClient = get(queryClientAtom)
  const pwApiPrefix = get(pwapiEndpointPrefixAtom)
  const mcpPrefix = get(mcpEndpointPrefixAtom)
  const ctx: ClaimSpiritContext = {
    api: undefined,
    name: account?.name || 'unknown',
    address: account?.address,
    spiritCollection: [],
    isConnected: false,
    canClaimSpirits: false,
    balance: new Decimal(0),
    hasInited: false,
    queryClient,
    pwApiPrefix,
    mcpPrefix,
  }
  return createMachine<ClaimSpiritContext>(
    makeConfig(ctx),
    makeOptions({
      services: {
        checkState: (ctx) => checkState(ctx),
        createSpirit: (ctx) => createSpirit(ctx),
        updateSpirit: (ctx) => updateSpirit(ctx),
        claimSpirit: (ctx) => claimSpirit(ctx),
        removeSpirit: ({ address }) => removeSpirit(address),
      }
    })
  )
})

const useTrackDepends = () => {
  const send = useSetAtom(claimSpiritMachineAtom)
  const machine = useAtomValue(websocketConnectionMachineAtom)
  const account = useAtomValue(currentAccountAtom)
  const signer = useAtomValue(signerAtom)
  const refCode = useRefCode()

  useEffect(() => {
    const api = machine.context.connection
    send({
      type: 'updateDependencies',
      api,
      name: account?.name || 'unknown',
      address: account?.address,
      isConnected: machine.matches('connected'),
      signer,
      refCode,
    })

    if (api && machine.value === 'connected' && account) {
      let unsubscribe: Function | null = null
      api.query.system.account(account.address, (balance) => {
        const { data: { free: freeBalance } } = balance
        const multiplier = new Decimal(10).pow(api.registry.chainDecimals[0])
        const value = new Decimal(freeBalance.toString() || '0').div(multiplier)
        send({
          type: 'updateBalance',
          balance: value,
        })
      }).then(_unsubscribe => {
        unsubscribe = _unsubscribe
      })
      return () => {
        if (unsubscribe) {
          unsubscribe()
        }
      }
    }
  }, [send, account, machine, signer, refCode])
}

export const useStartClaimSpiritMachine = () => {
  useRestoreLastSelectedAccount()
  useAutoConnect()
  useTrackDepends()
}
